import * as Sentry from '@sentry/react';

const SENSITIVE_KEYS = new Set([
  'password',
  'newPassword',
  'currentPassword',
  'confirmPassword',
  'token',
  'accessToken',
  'refreshToken',
  'image',
  'authorization',
  'cookie',
  'secret',
  'apiKey',
  'stripeToken',
]);

function scrub(input: unknown, depth = 0): unknown {
  if (depth > 6) return '[Truncated]';
  if (Array.isArray(input)) return input.map((v) => scrub(v, depth + 1));
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.has(k)) out[k] = '[Filtered]';
      else if (typeof v === 'string' && v.length > 2048)
        out[k] = `[Filtered: long string (${v.length} chars)]`;
      else out[k] = scrub(v, depth + 1);
    }
    return out;
  }
  return input;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  // Local dev runs (vite dev server, Electron shell) only produce noise like
  // "Network Error" when the backend isn't running - keep Sentry prod/stage only.
  if (!dsn || import.meta.env.DEV) return;

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    import.meta.env.MODE;

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
      }
      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrub(event.request.data) as Record<string, unknown>;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        if (breadcrumb.data && typeof breadcrumb.data === 'object') {
          const data = breadcrumb.data as Record<string, unknown>;
          if (typeof data.url === 'string' && data.url.includes('vision/extract-equipment-info')) {
            data.body = '[image base64 omitted]';
          }
        }
      }
      return breadcrumb;
    },
  });
}

export { Sentry };
